import React, { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { EnvelopeSimple, CircleNotch, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPasswordDialog({ open, onOpenChange }) {
  const { lang } = useLang();
  const T = (es, en) => (lang === "es" ? es : en);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const change = (o) => { if (!o) { setSent(false); setError(null); } onOpenChange(o); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || T("No se pudo enviar. Intenta de nuevo.", "Could not send. Try again."));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="sm:max-w-md" data-testid="forgot-modal">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-3">
            <EnvelopeSimple size={22} weight="duotone" className="text-accent-foreground" />
          </div>
          <DialogTitle className="text-2xl font-black tracking-tight">{T("¿Olvidaste tu contraseña?", "Forgot your password?")}</DialogTitle>
          <DialogDescription className="pt-2 text-sm leading-relaxed">
            {sent
              ? T("Si el correo está registrado, te llegará un enlace para crear una nueva contraseña. Revisa también la carpeta de correo no deseado. El enlace vence en 24 horas.",
                  "If the email is registered, you'll receive a link to create a new password. Check your spam folder too. The link expires in 24 hours.")
              : T("Escribe tu correo corporativo y te enviaremos un enlace para crear una nueva contraseña.",
                  "Enter your corporate email and we'll send you a link to create a new password.")}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="flex items-center gap-2 text-sm text-emerald-600 font-semibold" data-testid="forgot-sent">
            <CheckCircle size={18} weight="fill" /> {T("Solicitud enviada", "Request sent")}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
            <Label htmlFor="forgot-email">{T("Correo", "Email")}</Label>
            <Input id="forgot-email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="nombre@sdm.com.do" className="h-11" data-testid="forgot-email-input" />
            {error && <div className="text-xs text-destructive" data-testid="forgot-error">{error}</div>}
            <Button type="submit" disabled={busy || !email.includes("@")} className="w-full h-11" data-testid="forgot-submit-btn">
              {busy && <CircleNotch size={16} className="animate-spin mr-2" />}
              {T("Enviar enlace", "Send link")}
            </Button>
          </form>
        )}
        {sent && (
          <DialogFooter className="mt-2">
            <Button onClick={() => change(false)} className="w-full h-11" data-testid="forgot-close-btn">{T("Entendido", "Got it")}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
